import "./cart.scss";
import { useCallback, useState } from "react";
import axios from "axios";
import { Store as NotificationStore } from "react-notifications-component";
import { PHP_URL, STRAPI_URL } from "../../config/env";
import useCart from "../../hooks/useCart";
import Checkout from "./checkout";
import KontaktForm from "./contact";
import Overview from "./overview";
import Confirmation from "./confirmation";
const math = require("mathjs");

const STATES = ["overview", "contact", "checkout", "confirmation"];

export default function Cart() {
  const [state, setState] = useState(STATES[0]);
  const next = useCallback(() => {
    const currentIndex = STATES.indexOf(state);
    if (currentIndex === -1 || currentIndex === STATES.length - 1) {
      return;
    }
    setState(STATES[currentIndex + 1]);
  }, [state, setState]);
  const prev = useCallback(() => {
    const currentIndex = STATES.indexOf(state);
    if (currentIndex === -1 || currentIndex === 0) {
      return;
    }
    setState(STATES[currentIndex - 1]);
  }, [state, setState]);

  const cart = useCart();
  const [kontaktInformationen, setKontaktInformationen] = useState(null);
  const [bestellung, setBestellung] = useState(null);

  const [bestellungIsLoading, setBestellungIsLoading] = useState(null);

  const onBestellung = useCallback(
    async (paypalData) => {
      setBestellungIsLoading(true);
      let itemCounts = [];
      try {
        const response = await fetch(`${PHP_URL}/count.php?x=all`);
        const counts = await response.json();
        itemCounts = Array.isArray(counts) ? counts : [];
      } catch (error) {
        console.error("Error fetching item counts:", error);
      }

      const data = {
        data: {
          bestellnummer: paypalData?.bestellnummer,
          erstellt: new Date().toISOString(),
          shippingCost: cart.getShippingCost().toFixed(2),
          item_total: cart.getTotal(itemPreis),
          summe: math.add(cart.getTotal(itemPreis), cart.getShippingCost()),
          ShippingAddress: {
            address_line_1: `${kontaktInformationen?.strasse ?? "-"} ${kontaktInformationen?.hausnummer ?? ""}`.trim(),
            admin_area_1: "",
            admin_area_2: kontaktInformationen?.ort ?? "-",
            country_code: kontaktInformationen?.land ?? "-",
            postal_code: kontaktInformationen?.plz ?? "-",
          },
          billing_address: {
            address_line_1: `${kontaktInformationen?.strasse ?? "-"} ${kontaktInformationen?.hausnummer ?? ""}`.trim(),
            admin_area_1: "",
            admin_area_2: kontaktInformationen?.ort ?? "-",
            country_code: kontaktInformationen?.land ?? "-",
            postal_code: kontaktInformationen?.plz ?? "-",
          },
          Besteller: {
            name: kontaktInformationen?.lastname ?? "-",
            vorname: kontaktInformationen?.firstname ?? "-",
            email: kontaktInformationen?.email ?? "-",
            payerId: paypalData?.payer_id,
            RIGMitglied: isRabatt(),
            Abholung: isAbholung(),
            Newsletter: JSON.parse(
              window.sessionStorage.getItem("newsletter") ?? "false",
            ),
          },
          posten: cart.items.map((item) => ({
            productId: String(item.id),
            name: item.name,
            anzahl: Number(item.amount),
            preis: itemPreis(item),
            anzahlCamperPlatz: Number(item.amountCamper ?? 0),
            anzahlZeltPlatz: Number(item.amountTent ?? 0),
            option: item.option ?? "",
            grosse: item.size ?? "",
            type: item.type ?? "",
            countSoFar:
              itemCounts?.find(
                (c) =>
                  c.ProductId ===
                  `${item.id.toString()}-${item.size ?? ""}-${item.option ?? 0}`,
              )?.anzahl ?? 0,
          })),
        },
      };

      try {
        const bestellung = await axios.post(`${STRAPI_URL}/api/bestellungen`, data);
        data.data.id = bestellung.data.data.id;
        const hasETickets = data.data.posten.some((item) => item.type === "E-Tickets");
        setBestellung({
          id: data.data.id,
          bestellnummer: data.data.bestellnummer,
          hasETickets: hasETickets,
        });
        data.data.hasETickets = hasETickets;

        await axios.post(`${PHP_URL}/mailer.php`, data);

        // Erst nach erfolgreicher Persistierung und Mailversand den Warenkorb leeren.
        cart.clear();
        window.sessionStorage.removeItem("abholung");
        window.sessionStorage.removeItem("mitglied");
        window.sessionStorage.removeItem("newsletter");

        next();
        NotificationStore.addNotification({
          title: "Woopwoop",
          message: "Deine Bestellung war erfolgreich",
          type: "success",
          insert: "top",
          container: "top-center",
          animationIn: ["animate__animated", "animate__fadeIn"],
          animationOut: ["animate__animated", "animate__fadeOut"],
          dismiss: {
            duration: 10000,
            onScreen: true,
          },
        });
      } catch (error) {
        throwError(JSON.stringify(error));
      } finally {
        setBestellungIsLoading(false);
      }
    },
    [cart, setBestellung, kontaktInformationen, next],
  );

  return (
    <div className="cartBox">
      <div className="content">
        <div className="CartContainer">
          {state === "overview" && <Overview onNext={next} />}
          {state === "contact" && (
            <KontaktForm
              data={kontaktInformationen}
              onSubmit={setKontaktInformationen}
              onNext={next}
              onPrev={prev}
              withAdresse={!isAbholung() && cart.hasShipping()}
            />
          )}
          {state === "checkout" && (
            <Checkout bestellungIsLoading={bestellungIsLoading} onNext={next} onPrev={prev} onBestellung={onBestellung} />
          )}
          {state === "confirmation" && (
            <Confirmation bestellung={bestellung} />
          )}
        </div>
      </div>
    </div>
  );
}

export function isAbholung() {
  const abholungFlag = window.sessionStorage.getItem("abholung");
  return abholungFlag === "true";
}

export function isRabatt() {
  const mitgliederRabatFlag = window.sessionStorage.getItem("mitglied");
  return mitgliederRabatFlag === "true";
}

export function itemPreis(item) {
  const rabatProItem = 0.8;
  return (
    isRabatt() && item.mitgliederRabat
      ? math.multiply(item.preis, rabatProItem)
      : item.preis
  ).toFixed(2);
}

export function throwError(error) {
  NotificationStore.addNotification({
    title: "Upps",
    message: error ?? "Irgendetwas lief schief",
    type: "danger",
    insert: "top",
    container: "top-center",
    animationIn: ["animate__animated", "animate__fadeIn"],
    animationOut: ["animate__animated", "animate__fadeOut"],
    dismiss: {
      duration: 3000,
    },
  });
}

export function Footer(props) {
  const { disabled, type = "button", onNext, onPrev } = props;
  return (
    <div className="CartFooter">
      {(onNext || type === "submit") && <button className="button primary" onClick={onNext ?? null} type={type} disabled={disabled}>Weiter</button>}
      {onPrev && <button className="button secondary" onClick={onPrev}>Zurück</button>}
    </div>
  );
}

